import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ElasticButton from '../ui/ElasticButton';

const CTASection = () => {
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

    return (
        <section
            ref={sectionRef}
            className="py-32 bg-cyber-black text-center relative z-10 border-t border-gray-800 overflow-hidden"
        >
            {/* Animated Floating Orbs */}
            <div className="absolute inset-0 pointer-events-none">
                <motion.div
                    className="absolute top-20 left-1/4 w-64 h-64 rounded-full opacity-20"
                    style={{
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.6) 0%, transparent 70%)',
                        filter: 'blur(40px)'
                    }}
                    animate={{
                        y: [0, -30, 0],
                        x: [0, 20, 0],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <motion.div
                    className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full opacity-20"
                    style={{
                        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, transparent 70%)',
                        filter: 'blur(40px)'
                    }}
                    animate={{
                        y: [0, 40, 0],
                        x: [0, -30, 0],
                        scale: [1, 1.15, 1],
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
                <motion.div
                    className="absolute top-1/2 right-1/3 w-48 h-48 rounded-full opacity-15"
                    style={{
                        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.6) 0%, transparent 70%)',
                        filter: 'blur(30px)'
                    }}
                    animate={{
                        y: [0, -20, 0],
                        x: [0, 15, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        duration: 7,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 2
                    }}
                />
            </div>

            <div className="max-w-5xl mx-auto px-4 relative z-10">
                {/* Eyebrow Text */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-6"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    <span className="text-sm font-medium text-blue-300 tracking-wide">
                        Join 10,000+ developers building smarter
                    </span>
                </motion.div>

                {/* Main Heading with Gradient Shimmer */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    className="mb-6"
                >
                    <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-4 relative inline-block">
                        <span className="relative z-10 bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent animate-gradient-x">
                            Ready to transform
                        </span>
                    </h2>
                    <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight relative inline-block">
                        <span className="relative z-10 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x">
                            your workflow?
                        </span>
                    </h2>
                </motion.div>

                {/* Supporting Text */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed"
                >
                    Build, visualize, and automate complex data pipelines in minutes.
                    <br />
                    <span className="text-blue-300">No code required. Just pure flow.</span>
                </motion.p>

                {/* Feature Pills */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                    className="flex flex-wrap justify-center gap-3 mb-12"
                >
                    {[
                        { icon: "⚡", text: "Setup in 60 seconds" },
                        { icon: "🎯", text: "Drag & drop simplicity" },
                        { icon: "🔒", text: "Enterprise-grade security" },
                        { icon: "🚀", text: "Unlimited pipelines" }
                    ].map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={isInView ? { opacity: 1, scale: 1 } : {}}
                            transition={{
                                duration: 0.5,
                                delay: 0.7 + (index * 0.1),
                                ease: "easeOut"
                            }}
                            whileHover={{
                                scale: 1.05,
                                boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)"
                            }}
                            className="px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm hover:border-blue-500/50 transition-all duration-300"
                        >
                            <span className="text-sm font-medium text-gray-300">
                                <span className="mr-2">{feature.icon}</span>
                                {feature.text}
                            </span>
                        </motion.div>
                    ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
                    className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
                >
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="relative"
                    >
                        {/* Pulsing glow effect */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full opacity-30 blur-lg animate-pulse"></div>
                        <ElasticButton primary>
                            <span className="flex items-center gap-2">
                                Start Building Free
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </ElasticButton>
                    </motion.div>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-8 py-4 rounded-full font-bold text-lg bg-transparent border-2 border-gray-700 text-gray-300 hover:border-blue-500 hover:text-white transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20"
                    >
                        <span className="flex items-center gap-2 justify-center">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                            Watch Demo
                        </span>
                    </motion.button>
                </motion.div>

                {/* Trust Indicators */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1, delay: 1.2, ease: "easeOut" }}
                    className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-500"
                >
                    <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 border-2 border-gray-900"></div>
                            ))}
                        </div>
                        <span className="text-gray-400">
                            <span className="font-semibold text-white">10,000+</span> active users
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                </svg>
                            ))}
                        </div>
                        <span className="text-gray-400">
                            <span className="font-semibold text-white">4.9/5</span> rating
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-400">
                            <span className="font-semibold text-white">SOC 2</span> compliant
                        </span>
                    </div>
                </motion.div>
            </div>

            {/* Add custom CSS for gradient animation */}
            <style jsx>{`
                @keyframes gradient-x {
                    0%, 100% {
                        background-position: 0% 50%;
                    }
                    50% {
                        background-position: 100% 50%;
                    }
                }
                .animate-gradient-x {
                    background-size: 200% 200%;
                    animation: gradient-x 3s ease infinite;
                }
            `}</style>
        </section>
    );
};

export default CTASection;
